import React from 'react';
import { motion } from 'framer-motion';
import { Info } from 'lucide-react';
import { fadeInUp, staggerContainer } from '../hooks/useScrollAnimation';
import SectionHeading from './common/SectionHeading';
import GlassPanel from './common/GlassPanel';
import Button from './common/Button';
import { FORECAST_URL } from '../config/links';

/**
 * These figures are modelled, not observed. This section previously ran under a
 * "Case Study" eyebrow with the heading "Real Projections. Real Revenue.",
 * which invited the reader to take them as a delivered client result. The
 * numbers are unchanged — the framing now says plainly what they are, and the
 * inputs behind them are shown so a club can sanity-check the model.
 */
const assumptions = [
  { label: 'Average attendance', value: '25,000' },
  { label: 'Addressable supporter base', value: '~120,000' },
  { label: 'Card activation rate', value: '10%' },
  { label: 'Modelled period', value: 'First 12 months' },
];

const outputs = [
  { value: 'EUR 3.2M', label: 'Modelled annual revenue' },
  { value: '12,000+', label: 'Card activations in year one' },
  { value: '380+', label: 'Merchant partners in network' },
  { value: '642%', label: 'Modelled sponsor ROI' },
];

const CaseData = () => {
  return (
    <section className="relative py-24 md:py-32 bg-esf-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Revenue Model"
          headline="What the numbers look like"
          subheadline="A worked example for a mid-tier European club, built from the same model behind the forecast tool."
        />

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="mt-16"
        >
          <motion.div variants={fadeInUp}>
            <GlassPanel className="p-8 md:p-12" hover={false}>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 pb-10 border-b border-white/[0.06]">
                {assumptions.map((assumption) => (
                  <div key={assumption.label}>
                    <p className="text-body-sm text-esf-gray-500">{assumption.label}</p>
                    <p className="mt-1 text-body-lg font-semibold text-esf-white">
                      {assumption.value}
                    </p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 pt-10">
                {outputs.map((output) => (
                  <div key={output.label}>
                    <p className="text-2xl md:text-3xl font-bold text-gradient">{output.value}</p>
                    <p className="text-body-sm text-esf-gray-500 mt-2">{output.label}</p>
                  </div>
                ))}
              </div>

              <p className="mt-10 flex items-start gap-2.5 text-body-sm text-esf-gray-500 leading-relaxed">
                <Info size={16} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span>
                  Illustrative projection based on the assumptions above, not a
                  delivered client result. Actual revenue depends on your supporter
                  base, market and activation rate. Not a financial promise or an
                  offer of credit.
                </span>
              </p>

              <div className="mt-8">
                <Button href={FORECAST_URL} variant="primary" size="md">
                  Model your own club
                </Button>
              </div>
            </GlassPanel>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default CaseData;
